<template>
  <div id="activities-agenda-index">
    <Breadcrumbs
      :breadcrumbs="[
        { title: this.$t(`menu.${this.serviceSlug}`)},
        { title: this.$t('activities_agenda.agenda.title'), disabled: true }
      ]">

    </Breadcrumbs>

    <fade-transition
      :duration="400"
      mode="out-in">
      <div class="btn btn__floating btn__round__large btn__round__large"
           v-if="canCreateOrUpdateEvent">
        <va-popover
          :message="$t('activities_agenda.agenda.new_event')"
          placement="left"
        >
          <va-button
            :disabled="isLoading"
            @click.prevent="createWithDefaults">
            <i class="fa fa-plus"></i>
          </va-button>

        </va-popover>

      </div>
    </fade-transition>

    <div class="evollu-form" style="z-index: 4">
      <div class="form-row">
        <div class="col-12 col-md-4">
          <div>
            <label class="form-label">{{$t('entities.activities.revision.activity')}}:</label>
            <multiselect
              v-model="filters.activity"
              :options="activitiesOptions"
              track-by="id"
              :customLabel="elem => $t(`entities.activity_types.${elem.id}`)"
              select-label=""
              selected-label=""
              deselect-label=""
              :placeholder="$t('entities.activities.revision.activity')"
              :allow-empty="true"
              :multiple="false"
              :searchable="true"
              :disabled="activitiesOptions.length === 1 || isLoading">
              <template slot="clear">
                <span class="multiselect__clear" v-if="filters.activity"
                      @mousedown.prevent.stop="clearActivitiesMultiselect"></span>
              </template>
            </multiselect>
          </div>
        </div>

        <div class="col-12 col-md-4">
          <div>
            <label class="form-label">{{$t('facility.inputs.entity')}}:</label>
            <multiselect
              v-model="filters.entity"
              :options="entitiesOptions"
              track-by="id"
              label="name"
              :custom-label="formatedEntityLabel"
              select-label=""
              selected-label=""
              deselect-label=""
              :placeholder="$t('facility.inputs.entity')"
              :allow-empty="true"
              :multiple="false"
              :searchable="true"
              :disabled="entitiesOptions.length === 1 || filters.activity === null || isLoading">
              <template slot="clear">
                <span class="multiselect__clear" v-if="filters.entity"
                      @mousedown.prevent.stop="clearEntitiesMultiselect"></span>
              </template>
            </multiselect>
          </div>
        </div>

        <div class="col-12 col-md-4">
          <div>
            <label class="form-label">{{$t('agenda.appointments.facility')}}:</label>
            <multiselect
              v-model="filters.facility"
              :options="facilitiesOptions"
              track-by="id"
              label="name"
              :custom-label="formatedEntityLabel"
              select-label=""
              selected-label=""
              deselect-label=""
              :placeholder="$t('agenda.appointments.facility')"
              :allow-empty="true"
              :multiple="false"
              :searchable="true"
              :disabled="facilitiesOptions.length <= 1 || isLoading">
              <template slot="clear">
                <span class="multiselect__clear" v-if="filters.facility"
                      @mousedown.prevent.stop="clearFacilitiesMultiselect"></span>
              </template>
            </multiselect>
          </div>
        </div>
      </div>


    </div>

    <div class="calendar-wrapper">
      <div v-if="isLoading" class="loading-overlay">
        <LoadingPlaceholder />
      </div>
      <div class="simple-overlay" v-if="filters.entity === null"/>
      <FullCalendar
        id="activitiesEventsAgenda"
        :key="calendarKey"
        :loading="isLoading"
        ref="fullCalendar"
        :options="calendarOptions">
        <template v-slot:eventContent='data'>
          <AgendaEvent :event="data.event" :view="data.view"/>
        </template>

      </FullCalendar>
    </div>


    <EventForm
      v-if="showEventFormModal"
      v-bind="propsNewEventForm"
      :service="component.service"
      @modal-closed="modalClosed"
      @eventCreated="updateCalendar"
      @eventDeleted="eventDeleted"
      @redirectToPm="redirectToPm"
      @cancel="modalClosed"/>

    <EventApprovalModal
      :service="component.service"
      v-if="showApprovalModal"
      v-bind="propsNewEventForm"
      @cancel="modalClosed"
      @eventApproved="updateCalendar"
      @eventRejected="eventDeleted"
      @modal-closed="modalClosed"
      @redirectToPm="redirectToPm"
    />
    <EventApprovalModal
      :service="component.service"
      v-if="showConsultationModal"
      :title="$t('activities_agenda.approval.approved_title')"
      :showActions="false"
      v-bind="propsNewEventForm"
      @cancel="modalClosed"
      @redirectToPm="redirectToPm"
    />


  </div>
</template>

<script>
import axios from 'axios'
import Breadcrumbs from "@/components/common/Breadcrumbs.vue";
import Loading from "@/components/common/loaders/Loading.vue";
import LoadingPlaceholder from "@/components/common/LoadingPlaceholder.vue";
import moment from 'moment'
import ApiService from "@/api/ApiService";
import FullCalendar from '@fullcalendar/vue'
import momentPlugin from '@fullcalendar/moment'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from "@fullcalendar/interaction";
import LocalStorageHelper from "@/local-storage/LocalStorageHelper";
import Multiselect from 'vue-multiselect'

import {
  FILTERS_ACTIVITIES_AGENDA_CALENDAR,
  FILTERS_CALENDAR_DAY,
  FILTERS_CALENDAR_VIEW
} from "@/local-storage/LocalStorageKeys";
import MultiselectLabelFormatter from "@/mixins/MultiselectLabelFormatter.vue";
import { ActivitiesAgendaService } from "@/api/ActivitiesAgendaService";
import AgendaEvent from "@/components/activities_agenda/agenda/AgendaEvent.vue";
import { FORM_DEFAULTS } from "@/components/clinical/appointments/config";
import EventForm from "@/components/activities_agenda/agenda/EventForm.vue";
import EventApprovalModal from "@/components/activities_agenda/agenda/EventApprovalModal.vue";
import FadeTransition from "@/components/common/transitions/FadeTransition.vue";
import { ActivityService } from "@/api/AuthService";
import { uniqBy } from "lodash";

const ActivitiesAgendaAgendaInfoType = 100
const accessTypeRead = 1
const accessTypeWrite = 2
const calendarMinTime = '08:00:00'
const calendarMaxTime = '20:00:00'

const EntityActivityUserActiveStatus = 1

export default {
  props: {
    serviceSlug: {
      type: String,
      required: true
    },
    component: {
      type: Object,
      required: true
    }
  },
  mixins: [
    MultiselectLabelFormatter
  ],
  components: { FadeTransition, EventForm, AgendaEvent, LoadingPlaceholder, Loading, Breadcrumbs, FullCalendar, Multiselect, EventApprovalModal },
  data: function() {
    return {
      isLoading: false,
      agendaEvents: [],
      entitiesOptions: [],
      facilitiesOptions: [],
      activitiesOptions: [],
      activitiesOptionsFull: [],
      occupiedTimeSlots: [],
      filters: {
        activity: null,
        entity: null,
        facility: null
      },
      calendarKey: 0,
      cancelationEventsCallback: null,
      cancelationReadCallback: null,
      cancelationWriteCallback: null,
      hasLoadedLocalStorage: false,
      calendarOptions: {
        eventSources: [{
          events: (options, callback) => {
            if (this.filters.activity !== null && this.filters.entity !== null) {
              this.isLoading = true
              /*if (this.cancelationEventsCallback !== null) {
                this.cancelationEventsCallback()
              }*/

              let params = {
                start: moment(options.start).format('YYYY-MM-DD'),
                entity: this.filters.entity.id,
                activity: this.filters.activity.id
              }

              if (this.filters.facility !== null) {
                params.facility = this.filters.facility.id
              }

              if (moment(options.end).format('HH:mm:ss') === '00:00:00') {
                params.end = moment(options.end).subtract(1, 'second').format('YYYY-MM-DD')
              }

              this.isLoading = true
              ApiService.query(`${this.component.service.url}/v1/bookings`, params)
                .then(response => {
                  this.agendaEvents = response.data.map(event => ({
                    ...event,
                    start: this.$options.filters.localTime(event.start),
                    end: this.$options.filters.localTime(event.end),
                  }))

                  callback(this.agendaEvents.map(event => ({
                    ...event,
                    borderColor: event.bookingType && event.bookingType.color ? event.bookingType.color : '#29c4a9',
                    className: this.isUnapprovedEvent(event) ? 'background-striped' : this.isPrivatlyOccupiedSlot(event) ? 'private-occupied-slot' : 'normal-event'
                  })))

                })
                .catch(errors => {
                  console.log(errors)
                })
                .finally(() => {
                  //this.cancelationReadCallback = null
                  this.isLoading = false
                })
            }
          }
        }],
        lazyFetching: false,
        eventColor: '#29c4a9',
        plugins: [dayGridPlugin, timeGridPlugin, momentPlugin, interactionPlugin],
        dayMaxEventRows: 3,
        headerToolbar: {
          left: 'title',
          right: 'timeGridDay timeGridWeek dayGridMonth prev next'
        },
        eventDisplay: 'block',
        buttonText: {
          today: this.$t('agenda.buttons.today'),
          month: this.$t('agenda.buttons.month'),
          week: this.$t('agenda.buttons.week'),
          day: this.$t('agenda.buttons.day'),
          listMonth: this.$t('agenda.buttons.listMonth'),
        },
        customButtons: {
          timeGridDay: {
            text: this.$t('agenda.buttons.day'),
            click: (event) => {
              this.calendarAPI.gotoDate(this.getNextViewStart())
              this.calendarAPI.changeView('timeGridDay')
            }
          },
          timeGridWeek: {
            text: this.$t('agenda.buttons.week'),
            click: () => {
              this.calendarAPI.gotoDate(this.getNextViewStart())
              this.calendarAPI.changeView('timeGridWeek')
            }
          }
        },
        initialDate: LocalStorageHelper.get(FILTERS_CALENDAR_DAY, false),
        initialView: LocalStorageHelper.get(FILTERS_CALENDAR_VIEW) || 'dayGridMonth',
        locale: this.selectedLanguage,
        firstDay: 1,
        hiddenDays: [0],
        allDayContent: this.$t('agenda.buttons.all-day'),
        allDayClassNames: 'calendar__allday__label',
        slotMinTime: calendarMinTime,
        slotMaxTime: calendarMaxTime,
        titleFormat: 'MMMM, YYYY',
        displayEventEnd: true,
        nowIndicator: true,
        slotDuration: '00:30',
        slotLabelFormat: 'HH:mm',
        slotLabelInterval: '01:00',
        height: 'auto',
        dayHeaderFormat: 'ddd D',
        eventTimeFormat: 'H:mm',
        editable: true,
        weekends: true,
        allDaySlot: false,
        selectable: true,
        selectMirror: true,
        unselectAuto: true,
        navLinks: true,
        dayHeaderContent: (content) => {
          return {
            html: `
          <div class="calendar__header__label">
            <div class="calendar__header__label__day view__${content.view.type}">` + moment(content.date).locale(this.selectedLanguage === 'gb' ? 'en' : this.selectedLanguage).format('DD') + `</div>
            <div class="calendar__header__label__weekday">` + moment(content.date).locale(this.selectedLanguage === 'gb' ? 'en' : this.selectedLanguage).format('dddd') + `</div>
          </div>
        `
          }
        },
        eventClick: this.eventSelected,
        eventDrop: this.updateEventFromUI,
        select: this.eventCreated,
        navLinkDayClick: this.dayClicked,
        datesSet: this.datesDidUpdate
      },
      calendarAPI: null,
      propsNewEventForm: {},
      showEventInfoModal: false,
      showEventFormModal: false,
      showApprovalModal: false,
      showConsultationModal: false,
    }
  },
  created: function () {

  },
  mounted: async function() {
    await this.loadActivitiesOptions()
    this.calendarAPI = this.$refs.fullCalendar.getApi()
    if (this.calendarAPI) {
      this.calendarAPI.setOption('locale', this.selectedLanguage)
    }

    /*if (!LocalStorageHelper.get(FILTERS_ACTIVITIES_AGENDA_CALENDAR)) {
      await this.persistFilters()
    }*/
    await this.loadEvents()

  },
  methods: {
    redirectToPm(data) {
      this.modalClosed()
      this.$router.replace({ name: `people_manager/people-manager/list/patient/id`, params: { id: data.id } });
    },
    loadActivitiesOptions: async function() {
      try {
        const response = await ActivityService.getActivitiesAgendaOptions()
        this.activitiesOptionsFull = response
        this.activitiesOptions = response.internal.map(elem => elem.activity)
        this.activitiesOptions.push(...response.external.map(elem => elem.activity))
        this.activitiesOptions = uniqBy(this.activitiesOptions, 'id')

        if(this.activitiesOptions.length === 1) {
          this.filters.activity = this.activitiesOptions[0]
        }

      } catch (err) {
        console.log(err)
        this.showToast(this.$t('entities.messages.error'), this.$store.state.errorToastOptions)
      }

    },
    eventDeleted: function () {
      this.propsNewEventForm = {}
      this.showApprovalModal = false
      this.showEventFormModal = false
      this.loadEvents()
    },
    getNextViewStart: function () {
      let start = moment(this.calendarAPI.view.currentStart)
      let end = moment(this.calendarAPI.view.currentEnd)
      let now = moment()
      if (start.isSameOrBefore(now) && end.isSameOrAfter(now)) {
        return now.toISOString()
      }
      return start.toISOString()
    },
    updateCalendar: function () {
      this.showEventFormModal = false
      this.showApprovalModal = false
      this.propsNewEventForm = {}
      this.loadEvents()
    },
    modalClosed: function () {
      this.propsNewEventForm = {}
      this.showConsultationModal = false
      this.showApprovalModal = false
      this.showEventFormModal = false
    },
    eventSelected: function ({event}) {
      if(!this.canCreateOrUpdateEvent) {
        this.calendarAPI.unselect()
        return
      }

      let facilities = []
      if(this.filters.facility) {
        facilities.push(this.filters.facility)
      } else if(this.filters.entity && this.entitiesWrite.some(ent => ent.id === this.filters.entity.id)) {
        facilities = this.filters.entity.facilities
      }

      /*if (!facilities.length) {
        this.calendarAPI.unselect()
        return
      }*/

      this.propsNewEventForm = {
        event: {
          id: event.id,
          ...event.extendedProps,
          source: null,
          start: moment(event.start).format('YYYY-MM-DD HH:mm:ss'),
          end: moment(event.end).format('YYYY-MM-DD HH:mm:ss'),
        },
        facilities: facilities,
        selectedActivity: this.filters.activity,
        selectedEntity: this.filters.entity,
        entitiesRead: this.entitiesRead,
        entitiesWrite: this.entitiesWrite,
        agendaEvents: this.agendaEvents,
        isExternalBooking: this.isExternalBooking,
      }

      if (event.extendedProps.createdBy !== this.$auth.user().id && event.extendedProps.originFacility.id !== event.extendedProps.facility.id
        && event.extendedProps.approvedAt === null) {
        //event is still pending approval, show modal for approval
        this.showApprovalModal = true
      } else if( (event.extendedProps.createdBy === this.$auth.user().id || this.$auth.user().entity_id !== event.extendedProps.entityId) && event.extendedProps.originFacility.id !== event.extendedProps.facility.id
        && event.extendedProps.approvedAt !== null ) {
        //user is the owner of the event, the event is for an external entity and has been approved, thus, it does not belong to him now
        this.showConsultationModal = true
      } else {
        this.showEventFormModal = true
      }


    },
    clearActivitiesMultiselect: function () {
      this.filters.activity = null
      this.clearEntitiesMultiselect()
    },
    clearEntitiesMultiselect: function () {
      this.filters.entity = null
      this.filters.facility = null
      this.facilitiesOptions = []
      this.$refs.fullCalendar.getApi().removeAllEvents()
    },
    clearFacilitiesMultiselect: function () {
      this.filters.facility = null
    },
    loadLocalStorage: function () {
      return new Promise(resolve => {
        let state = LocalStorageHelper.get(FILTERS_ACTIVITIES_AGENDA_CALENDAR)

        let filters = {
          entity: null,
          facility: null,
        }
        if (state !== null) {
          if (state.hasOwnProperty('activity') && state.activity) {
            this.filters.activity = this.activitiesOptions.find(elem => elem.id === state.activity)
          }

          if (state.hasOwnProperty('entity') && state.entity) {
            filters.entity = this.entitiesOptions.find(elem => elem.id === state.entity)
            this.facilitiesOptions = filters.entity.facilities
          }
          if (state.hasOwnProperty('facility') && state.facility) {
            filters.facility = this.facilitiesOptions.find(elem => elem.id === state.facility)
          }
        }
        this.filters = { ...this.filters, ...filters }

        this.hasLoadedLocalStorage = true
        resolve()
      })
    },
    persistFilters: function () {
      let filters = {
        activity: this.filters.activity !== null ? this.filters.activity.id : null,
        entity: this.filters.entity !== null ? this.filters.entity.id : null,
        facility: this.filters.facility !== null ? this.filters.facility.id : null
      }

      LocalStorageHelper.set(FILTERS_ACTIVITIES_AGENDA_CALENDAR, filters)
    },
    loadEvents: async function() {
      if(this.calendarAPI) {
        this.calendarAPI.removeAllEvents()
        await this.calendarAPI.refetchEvents()
      }
    },
    datesDidUpdate: function () {
      setTimeout(() => {
        this.$nextTick( () => {
          //await this.loadLocalStorage()
          LocalStorageHelper.set(FILTERS_CALENDAR_VIEW, this.calendarAPI.view.type)
          LocalStorageHelper.set(FILTERS_CALENDAR_DAY, this.moment(this.calendarAPI.view.currentStart).format('YYYY-MM-DD'), false)
        })
      }, 1000)

    },
    isPrivatlyOccupiedSlot: function (event) {
      return event.showAsPrivateOccupiedSlot === true
    },
    isUnapprovedEvent: function (event) {
      return (event.showAsPrivateOccupiedSlot === false && event.facility.id !== event.originFacility.id) && (event.approvedAt === null)
    },
    getDefaults: function() {
      if (!this.calendarAPI) {
        return
      }
      /*let activeStart = moment(this.getNextViewStart())
      let start = moment(`${activeStart.format('YYYY-MM-DD')} ${this.calendarOptions.slotMinTime}`)
      let end = start.clone().add(30, FORM_DEFAULTS.INCREMENT_INTERVAL)*/
      const start = moment()
      const fixedStart = start.add(30 - (start.minute() % 30), 'minutes').startOf('minute');
      const end = fixedStart.clone().add(30, FORM_DEFAULTS.INCREMENT_INTERVAL)
      return {
        start: fixedStart.format('YYYY-MM-DD HH:mm:ss'),
        end: end.format('YYYY-MM-DD HH:mm:ss'),
        allDay: true,
        fromDefault: true
      }
    },
    createWithDefaults: function () {
      let params = this.getDefaults()
      this.eventCreated(params)
    },
    updateEventFromUI: function ({ event, revert }) {
      try {
        if (!this.canCreateOrUpdateEvent) {
          console.log("Missing Perms")
          throw new Error('Missing permissions')
        }

        const eventId = parseInt(event.id)
        const agendaEvent = this.agendaEvents.find(event => event.id === eventId)

        if (agendaEvent === undefined) {
          throw new Error('Could not find an event with the given id')
        }

        const params = {
          ...agendaEvent,
          originEntityId: agendaEvent.entity.id,
          entityId: agendaEvent.entity.id,
          originFacilityId: agendaEvent.facility.id,
          facilityId: agendaEvent.facility.id,
          bookingTypeId: agendaEvent.bookingType.id,
          activityId: agendaEvent.activity.id,
          personId: agendaEvent.person.id,
          start: this.$options.filters.utcTime(event.start),
          end: this.$options.filters.utcTime(event.end)
        }

        if (params.paymentType !== null) params.paymentTypeId = params.paymentType.id


        ActivitiesAgendaService.updateEvent(event.id, params)
          .then(response => {
            this.showToast(this.$t('activities_agenda.agenda.update_event.success'), this.$store.state.successToastOptions)
          })
          .catch(errors => {
            console.log(errors)
            this.showToast(this.$t('activities_agenda.agenda.update_event.error'), this.$store.state.errorToastOptions)
            revert()
          })
      } catch (errors) {
        console.log(errors)
        revert()
      }
    },
    eventCreated: function({start, end, allDay, fromDefault = false}) {
      if(!this.canCreateOrUpdateEvent) {
        this.calendarAPI.unselect()
        return
      }

      let facilities = []
      if(this.filters.facility) {
        facilities.push(this.filters.facility)
      } else if(this.filters.entity && this.entitiesWrite.some(ent => ent.id === this.filters.entity.id)) {
        facilities = this.filters.entity.facilities
      }

      /*if (!facilities.length) {
        this.calendarAPI.unselect()
        return
      } */

      this.propsNewEventForm = {
        facilities
      }

      if(start && end) {
        start = moment(start)
        end = moment(end)

        if(this.calendarAPI.view.type === 'dayGridMonth') {
          let current = moment()
          start.set({
            hour: current.hour(),
            minute: current.minute(),
            second: 0
          })
          start = start.add(30 - (start.minute() % 30), 'minutes').startOf('minute');
          end = start.clone().add(30, FORM_DEFAULTS.INCREMENT_INTERVAL)
        }


        this.propsNewEventForm.fromEvent = {
          start: start.format('YYYY-MM-DD HH:mm:ss'),
          end: end.format('YYYY-MM-DD HH:mm:ss')
        }
        this.propsNewEventForm.selectedActivity = this.filters.activity
        this.propsNewEventForm.selectedEntity = this.filters.entity
        this.propsNewEventForm.selectedFacility = this.filters.facility
        this.propsNewEventForm.agendaEvents = this.agendaEvents
        this.propsNewEventForm.isExternalBooking = this.isExternalBooking

      }
      this.showEventFormModal = true
    }
  },
  computed: {
    selectedLanguage: function () {
      return this.$i18n.locale()
    },
    activitiesAgendaService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.activities_agenda_server)
    },
    entitiesRead: function () {
      const entitiesIds = this.activitiesAgendaService.user_permissions.filter(perm => perm.access_type >= accessTypeRead).map(elem => elem.entity_id)
      if(entitiesIds.some(elem => elem === null)) {
        return this.activitiesAgendaService.entities
      }
      return this.activitiesAgendaService.entities.filter(ent => entitiesIds.includes(ent.id) ) || []
    },
    entitiesWrite: function () {
      const entitiesIds = this.activitiesAgendaService.user_permissions.filter(perm => perm.access_type >= accessTypeWrite).map(elem => elem.entity_id)
      if(entitiesIds.some(elem => elem === null)) {
        return this.activitiesAgendaService.entities
      }
      return this.activitiesAgendaService.entities.filter(ent => entitiesIds.includes(ent.id) ) || []
    },
    canCreateOrUpdateEvent: function () {
      return this.filters.entity !== null && this.filters.activity !== null && this.activitiesAgendaService.user_permissions.some(perm => perm.info_type === 100 && perm.access_type >= 2)
      //return this.entitiesWrite.length > 0 && this.filters.entity !== null && this.entitiesWrite.some(ent => ent.id === this.filters.entity.id)
    },
    isExternalBooking: function () {
      if (this.filters.entity !== null) {
        return this.activitiesOptionsFull.internal.some(elem => elem.activity.id === this.filters.activity.id && elem.self_provider_entities.some(item => item.id === this.filters.entity.id)) === false &&
          this.activitiesOptionsFull.external.some(elem => elem.activity.id === this.filters.activity.id && elem.external_provider_entities.some(item => item.id === this.filters.entity.id)) === true
      }
      return false
    }
  },
  watch: {
    'filters': {
      handler: function() {
        this.persistFilters()
        this.loadEvents()
      },
      deep: true
    },
    'filters.activity': {
      handler: function(activity) {
        if (activity !== null) {
          this.filters.entity = null
          this.entitiesOptions = []
          const internalActivityOption = this.activitiesOptionsFull.internal.find(elem => elem.activity.id === activity.id)
          if(internalActivityOption) {
            this.entitiesOptions = [...internalActivityOption.self_provider_entities]
          }

          const externalActivityProviders = this.activitiesOptionsFull.external.find(elem => elem.activity.id === activity.id)
          if(externalActivityProviders) {
            this.entitiesOptions.push(...externalActivityProviders.external_provider_entities)
          }
          if(this.entitiesOptions.length === 1) {
            this.filters.entity = this.entitiesOptions[0]
          }
        } else {
          this.entitiesOptions = []
        }
      },
    },
    'filters.entity': {
      handler: function(entity) {
        if(entity !== null) {
          //Update facilities Options based on the selected entity
          this.filters.facility = null
          this.facilitiesOptions = entity.facilities
          if(this.facilitiesOptions.length === 1) {
            this.filters.facility = this.facilitiesOptions[0]
          }
        } else {
          this.facilitiesOptions = []
        }
      }

    },
    selectedLanguage: function (language) {
      if (this.calendarAPI) {
        this.calendarAPI.setOption('locale', language === 'gb' ? 'en' : language)
      }
    },
  }
}
</script>

<style lang='scss'>
// you must include each plugins' css
// paths prefixed with ~ signify node_modules
// @import '~@fullcalendar/core/main.css';
// @import '~@fullcalendar/daygrid/main.css';
// @import '~@fullcalendar/timegrid/main.css';
.private-occupied-slot {
  background-color: #834848 !important;
  border-color: #834848 !important;
  .fc-event-main {
    color: white !important;
  }
}

.normal-event {
  background-color: #e8e8e8 !important;
}

@mixin for-desktop {
  @media (min-width: 599px) {
    @content;
  }
}

#activities-agenda-index {

  .calendar-wrapper {
    position: relative;

    .vld-overlay {
      position: absolute;
      width: 100%;
      height: 100%;
      z-index: 500;

      .vld-background {
        position: absolute;
        width: 100%;
        height: 100%;
        background: rgb(245, 245, 245);
        opacity: 0.3;
      }

      .vld-icon {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }

  .fc-header-toolbar {
    button {
      background-color: transparent;
      -webkit-box-shadow: 0 0 4px 0 rgba(98, 106, 119, 0.25);
      box-shadow: 0 0 4px 0 rgba(98, 106, 119, 0.25);
      border-radius: 25px;
      border: none;
      color: #d4d4d4;
      padding-top: 0.3em;
      padding-bottom: 0.3em;
      padding-left: 1em;
      padding-right: 1em;

      &:hover {
        color: #6e6e6e;
      }

      &:not(:first-child) {
        margin-left: 1em;
      }

      // &:disabled {
      //   color:#d4d4d4;
      // }
      &.fc-button-active {
        color: #6e6e6e;
        font-weight: 600;
      }

      &.fc-prev-button {
        margin-left: 1.5em;
      }

      &.fc-next-button,
      &.fc-prev-button {
        padding: 0.3em;
        color: #6e6e6e;

        span {
          font-weight: bold;
          font-size: 1.3em;
        }
      }
    }

    .fc-toolbar-title {
      color: #414141;
      font-weight: 600;
      font-size: 1.5em;
    }
  }

  .fc-daygrid-event, .fc-timegrid-event {

    background-color: #e8e8e8;
    border-left-width: 10px;
    border-right-width: 0px;
    border-top-width: 0px;
    border-bottom-width: 0px;
    border-radius: 0%;

    .fc-event-main {
      color: #6E6E6E;
      padding-left: 5px;
      overflow: hidden;
    }
  }

  .person-name {
    color: #414141 !important;
    font-weight: 600;
  }

  .fc-timegrid-slot-label {
    color: #414141;
  }

  td.fc-timegrid-slot {
    border: 0.5px solid #d3d3d3 !important;
  }

  .fc-timegrid-cols td,
  .fc-timegrid-slot.fc-timegrid-slot-label,
  .fc-theme-standard .fc-scrollgrid,
  .fc-direction-ltr .fc-timeGridWeek-view .fc-scrollgrid tr > *:first-child,
  .fc-direction-ltr .fc-timeGridDay-view .fc-scrollgrid tr > *:first-child {
    border: none !important;
  }

  .fc-timeGridWeek-view td.fc-timegrid-slot.fc-timegrid-slot-lane,
  .fc-timeGridDay-view td.fc-timegrid-slot.fc-timegrid-slot-lane {
    // border-top: solid !important;
    border-right: none !important;
    border-left: none !important;
  }

  .fc-timeGridWeek-view tr:first-of-type td.fc-timegrid-slot.fc-timegrid-slot-lane,
  .fc-timeGridDay-view tr:first-of-type td.fc-timegrid-slot.fc-timegrid-slot-lane {
    border-top: 0.5px solid #d3d3d3 !important;
  }

  .fc-scrollgrid-section.fc-scrollgrid-section-header.fc-scrollgrid-section-sticky > td {
    border: none;
  }

  .fc-dayGridMonth-view .fc-scrollgrid-section.fc-scrollgrid-section-body > td {
    border-left: 0.5px solid #d3d3d3 !important;
    border-right: 0.5px solid #d3d3d3 !important;
    border-bottom: 0.5px solid #d3d3d3 !important;
  }

  .fc-scrollgrid-section.fc-scrollgrid-section-header.fc-scrollgrid-section-sticky > td {
    border-bottom: 0.5px solid #d3d3d3 !important;
  }

  .fc-scrollgrid-section-sticky > td {
    background-color: transparent;
  }

  th.fc-day-today, td.fc-day-today {
    background-color: #ebebeb;
  }

  .fc-day-grid-event > .fc-content {
    white-space: normal;
    text-overflow: ellipsis;
    max-height: 20px;
  }

  .fc-day-grid-event > .fc-content:hover {
    max-height: none !important;
  }

  .calendar__allday__label {
    font-size: 0.9rem;
  }

  .fc-header-left, fc-header-center, fc-header-right {
    width: 100%;
    display: block;
  }

  .floating__button {
    position: fixed;
    z-index: 9999;
    color: #29c4a9;
    font-size: 2rem;
  }

  .fc-col-header-cell {
    color: #d3d3d3;

    &.fc-day-today {
      color: #6E6E6E;
    }
  }

  .calendar__header__label {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    & .calendar__header__label__weekday {
      font-size: 0.6rem;
      text-transform: uppercase;
      @include for-desktop {
        font-size: 1.1rem;
      }
    }

    & .calendar__header__label__day {
      font-size: 0.8rem;
      font-weight: bold;
      @include for-desktop {
        font-size: 2.1rem;
      }

      &.view__dayGridMonth {
        display: none !important;
      }
    }
  }

  // .fc td, .fc th, .fc-timegrid, .fc-timeGridWeek-view, .fc-view {
  //   border-style: double !important;
  //   border-width: 0px !important;
  //   border-color: #aaa !important;
  // }

  .fc-col-header-cell {
    border-style: double !important;
    border-width: 0px !important;
    border-color: #aaa !important;
  }

  .fc-scrollgrid-section-header {
    border-left-style: double !important;
    border-left-width: 0px !important;
    border-left-color: #aaa !important;
  }

  .fc-timegrid-slot {
    height: 110px !important;
  }

  .fc-button-primary {
    color: #fff;
    background-color: #29c4a9;
    border-color: #29c4a9;
  }

  .fc-content {
    white-space: initial !important;
  }
}

#activities-agenda-index {
  & span.fa.fa-upload {
    font-size: 1.5rem;
    margin-right: 5px;

    &:hover {
      cursor: pointer;
    }
  }
}

.table-container {
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7); /* Semi-transparent white background */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999; /* Ensure it appears above the table */
}

.simple-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7); /* Semi-transparent white background */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3; /* Ensure it appears above the table */
}

.background-striped {
  //background: repeating-linear-gradient(45deg, #efceb4, #efceb4 10px, #d9d9d9 10px, #d9d9d9 20px)
  background: repeating-linear-gradient(45deg, #e8e8e8, #e8e8e8 10px, #e5d7a6 10px, #e5d7a6 20px)
}


</style>
